import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPaginationMetadata, ORDER_TYPE } from '@repo/ui';
import { GetAllPersonUseCaseInterface } from 'domain/usecases';
import { PERSON_ORDER_COLUMNS } from 'domain/value-objects/person-order-columns';

interface IPersonState {
  personsListData: GetAllPersonUseCaseInterface.Result;
  personsListHasNextPage: boolean;
  personsListPaginationMetadata: Omit<
    IPaginationMetadata<PERSON_ORDER_COLUMNS>,
    'searchString'
  >;
  personsListFilters: {
    cpf: string;
    name: string;
    email: string;
    profileType: number | null;
  };
}

interface IPayloadPersonsListData
  extends PayloadAction<GetAllPersonUseCaseInterface.Result> {}

export interface IPayloadChangePaginationMetadata
  extends PayloadAction<{
    key: keyof IPaginationMetadata<PERSON_ORDER_COLUMNS>;
    value: IPaginationMetadata<PERSON_ORDER_COLUMNS>[keyof IPaginationMetadata<PERSON_ORDER_COLUMNS>];
  }> {}

export interface IPayloadChangePersonsListPaginationMetadata
  extends PayloadAction<
    Partial<Omit<IPaginationMetadata<PERSON_ORDER_COLUMNS>, 'searchString'>>
  > {}

interface IPayloadPersonsListFilters
  extends PayloadAction<IPersonState['personsListFilters']> {}

const initialState: IPersonState = {
  personsListData: {
    total: 0,
    data: [],
  },
  personsListHasNextPage: false,
  personsListPaginationMetadata: {
    selectedPage: 0,
    rowsPerPage: 12,
    orderColumn: PERSON_ORDER_COLUMNS.NAME,
    sortDirection: ORDER_TYPE.ASC,
  },
  personsListFilters: {
    cpf: '',
    name: '',
    email: '',
    profileType: null,
  },
};

export const PersonSlice = createSlice({
  name: 'person',
  initialState,
  reducers: {
    loadPersonsListData: (state, action: IPayloadPersonsListData) => {
      const { data, total } = action.payload;
      const accumulatedData = [...state.personsListData.data, ...data];

      state.personsListData.total = total;
      state.personsListData.data = accumulatedData;
      state.personsListHasNextPage = accumulatedData.length < total;
    },
    clearPersonsListData: (state) => {
      state.personsListHasNextPage = false;
      state.personsListData = {
        total: 0,
        data: [],
      };
    },

    changePersonsListPaginationMetadata: (
      state,
      action: IPayloadChangePersonsListPaginationMetadata,
    ) => {
      state.personsListPaginationMetadata = {
        ...state.personsListPaginationMetadata,
        ...action.payload,
      };
    },
    clearPersonsListPaginationMetadata: (state) => {
      state.personsListPaginationMetadata = {
        selectedPage: 0,
        rowsPerPage: 12,
        orderColumn: PERSON_ORDER_COLUMNS.NAME,
        sortDirection: ORDER_TYPE.ASC,
      };
    },
    changePersonsListFilters: (state, action: IPayloadPersonsListFilters) => {
      state.personsListFilters = action.payload;
    },
  },
});

export const {
  loadPersonsListData,
  clearPersonsListData,
  changePersonsListFilters,
  clearPersonsListPaginationMetadata,
  changePersonsListPaginationMetadata,
} = PersonSlice.actions;

export default PersonSlice.reducer;
